import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { Sentry } from '@/lib/sentry';

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  errorReported: boolean;
}

export class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  ErrorBoundaryState
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorReported: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true };
  }

  async componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });

    // Report to Sentry first (no-ops if DSN is unset). Sentry also gets the
    // componentStack so we can see WHERE in the React tree it crashed.
    Sentry.captureException(error, {
      contexts: {
        react: { componentStack: errorInfo.componentStack },
      },
    });

    // Auto-report critical errors to feedback system
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        await supabase.from('feedback').insert({
          profile_id: user.id,
          feedback_type: 'bug',
          title: `[Auto-Report] ${error.name}: ${error.message}`,
          description: `
**Error Message:** ${error.message}

**Stack Trace:**
\`\`\`
${error.stack || 'No stack trace available'}
\`\`\`

**Component Stack:**
\`\`\`
${errorInfo.componentStack}
\`\`\`

**Browser:** ${navigator.userAgent}
**URL:** ${window.location.href}
**Timestamp:** ${new Date().toISOString()}
          `.trim(),
          page_url: window.location.href,
        });

        this.setState({ errorReported: true });
        console.log('Error automatically reported to feedback system');
      }
    } catch (reportError) {
      console.error('Failed to auto-report error:', reportError);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="text-destructive">Something went wrong</CardTitle>
              <CardDescription>
                The application encountered an unexpected error.
                {this.state.errorReported && (
                  <span className="block mt-2 text-primary text-xs">
                    ✓ Error has been automatically reported to our team
                  </span>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {process.env.NODE_ENV === 'development' && (
                <div className="text-sm bg-muted p-3 rounded-md overflow-auto max-h-40">
                  <div className="font-semibold text-destructive mb-2">
                    {this.state.error?.name}: {this.state.error?.message}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {this.state.error?.stack}
                  </div>
                </div>
              )}
              {!this.state.errorReported && (
                <div className="text-xs text-muted-foreground bg-muted/50 p-3 rounded">
                  💡 Tip: You can also report bugs using the feedback button to help us improve.
                </div>
              )}
              <Button
                onClick={() => {
                  this.setState({ hasError: false, error: null, errorInfo: null, errorReported: false });
                  window.location.reload();
                }}
                className="w-full"
              >
                Reload Application
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}